// src/components/DetailModal.tsx
import React from 'react';
import {X} from 'lucide-react';
import {fmtValue} from "../../utils/format";
import KpiCard, { KpiCardProps } from './KpiCard';


// ─── KPI card (clickable to toggle chart lines) ──────────────────────────────
export interface KpiItem extends Omit<KpiCardProps, 'icon'> {}

// ─── Section wrapper ─────────────────────────────────────────────────────────
export const ModalSection: React.FC<{
   title?: string;
   icon?: React.ReactNode;
   titleExtra?: React.ReactNode;
   border?: string;             // optional border color class
   className?: string;
   children: React.ReactNode;
}> = ({ title, icon, titleExtra, border, className, children }) => (
   <div className={`bg-gray-900 rounded-xl p-4 border ${border ?? 'border-gray-800'} ${className ?? ''}`}>
      {title && (
         <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-300">
               {icon && <span className="mr-1">{icon}</span>}{title}
            </h3>
            {titleExtra}
         </div>
      )}
      {children}
   </div>
);

// ─── Main DetailModal shell ──────────────────────────────────────────────────
export interface DetailModalProps {
   /** Called when the modal should close */
   onClose: () => void;
   /** Header config */
   header: {
      icon?: React.ReactNode;
      typeLabel?: string;            // small uppercase label above title
      title: string;
      subtitle?: React.ReactNode;
      bgClass?: string;              // e.g. 'bg-amber-900/20'
      borderClass?: string;          // e.g. 'border-amber-800/50'
      dotColor?: string;             // tailwind bg class for status dot
      dotPulse?: boolean;
   };
   /** Top‑level KPI row shown below the header */
   kpis?: KpiItem[];
   /** Optional hint text shown above / below KPIs (e.g. "click to toggle") */
   kpiHint?: string;
   /** Scrollable body sections */
   children: React.ReactNode;
   /** Max‑width tailwind class (default: max-w-3xl) */
   maxWidth?: string;
   /** Show a footer close button (default: true) */
   showFooter?: boolean;
}

const DetailModal: React.FC<DetailModalProps> = ({
   onClose,
   header,
   kpis,
   kpiHint,
   children,
   maxWidth = 'max-w-3xl',
   showFooter = true,
}) => {
   return (
      <div
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         onClick={onClose}
      >
         {/* Backdrop */}
         <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

         {/* Card */}
         <div
            className={`relative bg-gray-950 border border-gray-800 rounded-2xl w-full ${maxWidth} max-h-[92vh] shadow-2xl overflow-hidden flex flex-col`}
            onClick={e => e.stopPropagation()}
         >
            {/* ── Header ── */}
            <div
               className={`${header.bgClass ?? ''} ${header.borderClass ?? 'border-gray-800'} border-b px-3 md:px-6 py-3 md:py-5 flex items-start justify-between rounded-t-2xl`}
            >
               <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  {header.icon && (
                     <div className="text-2xl md:text-4xl shrink-0">{header.icon}</div>
                  )}
                  {header.dotColor && (
                     <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full mt-1 shrink-0 ${header.dotColor} ${header.dotPulse ? 'animate-pulse' : ''}`} />
                  )}
                  <div className="min-w-0">
                     {header.typeLabel && (
                        <div className="text-[10px] md:text-xs uppercase tracking-wider text-gray-500 mb-0.5 truncate">
                           {header.typeLabel}
                        </div>
                     )}
                     <h2 className="text-base md:text-xl font-bold text-white leading-tight truncate">
                        {header.title}
                     </h2>
                     {header.subtitle && (
                        <div className="mt-0.5 md:mt-1 text-xs md:text-sm">{header.subtitle}</div>
                     )}
                  </div>
               </div>
               <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-white transition-colors leading-none mt-1 ml-2 shrink-0"
               >
                  <X className="w-4 h-4 md:w-5 md:h-5" />
               </button>
            </div>


            {/* ── KPI row ── */}
            {kpis && kpis.length > 0 && (
               <div className="px-2 md:px-5 py-2 md:py-5 space-y-2 border-b border-gray-800">
                  {kpiHint && (
                     <p className="text-xs text-gray-500">{kpiHint}</p>
                  )}
                  <div
                     className="grid gap-2 md-gap-2"
                     style={{ gridTemplateColumns: `repeat(${Math.min(kpis.length, 6)}, minmax(0, 1fr))` }}
                  >
                     {kpis?.map((kpi, i) => (
                        <KpiCard key={i} {...kpi} />  // ← lowercase 'pi', no local const needed
                     ))}
                  </div>
               </div>
            )}
            
            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto p-2 md:p-5 space-y-5">
               {children}
            </div>

            {/* ── Footer ── */}
            {showFooter && (
               <div className="px-3 md:px-6 py-2 md:py-3 border-t border-gray-800 flex justify-end">
                  <button
                     onClick={onClose}
                     className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                     Close
                  </button>
               </div>
            )}
         </div>
      </div>
   );
};

export default DetailModal;
